import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Card, CardContent,
  Paper, Chip
} from '@mui/material';
import ReactPlayer from 'react-player';
import { motion } from 'framer-motion';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import LockIcon from '@mui/icons-material/Lock';

const signData = {
  aries: { symbol: '♈', color: '#FF6B6B', youtubeId: 'qGmfI0uqzx0' },
  taurus: { symbol: '♉', color: '#4ECDC4', youtubeId: 'JPwtuNcgW3U' },
  gemini: { symbol: '♊', color: '#FFE66D', youtubeId: '6wLIOkEIZZY' },
  cancer: { symbol: '♋', color: '#A8E6CF', youtubeId: 'mTv0GJKa_ac' },
  leo: { symbol: '♌', color: '#FFD93D', youtubeId: '6lVBoEhMQX4' },
  virgo: { symbol: '♍', color: '#95E1D3', youtubeId: '5lPWnFnj52s' },
  libra: { symbol: '♎', color: '#FFA8B5', youtubeId: 'IoECd8ex7GE' },
  scorpio: { symbol: '♏', color: '#B983FF', youtubeId: 'CTMyonhE_sU' },
  sagittarius: { symbol: '♐', color: '#FF8C42', youtubeId: 'pe515Q4bsXc' },
  capricorn: { symbol: '♑', color: '#6C5B7B', youtubeId: 'tr9_8jVxHfI' },
  aquarius: { symbol: '♒', color: '#4ECDC4', youtubeId: 'mKbLth2xSwk' },
  pisces: { symbol: '♓', color: '#C8A2C8', youtubeId: 'ciPMXa06axI' }
};

const CosmicOverview = () => {
  const { sign } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('admin') === 'true';

  const data = signData[sign?.toLowerCase()] || signData.aries;
  const color = data.color;

  return (
    <Box sx={{ bgcolor: '#0a0a0f', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
          py: 6,
          borderBottom: `4px solid ${color}`
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, textTransform: 'capitalize' }}>
            {sign} Rising - 2026 Cosmic Overview
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip
              label="Watch Free Overview"
              sx={{ bgcolor: '#4CAF50', color: 'white', fontWeight: 600, fontSize: '0.95rem', py: 2 }}
            />
            <Chip
              label="Get Extended Deep Dive"
              sx={{ bgcolor: '#c41e3a', color: 'white', fontWeight: 600, fontSize: '0.95rem', py: 2 }}
            />
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate(`/rising/${sign}${isAdminMode ? '?admin=true' : ''}`)}
            sx={{ mt: 3, borderColor: color, color: color }}
          >
            ← Back to {sign} Rising Overview
          </Button>
        </Container>
      </Box>

      {/* Free Video Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, px: { xs: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PlayCircleIcon sx={{ color: '#4CAF50', mr: 1, fontSize: 32 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Watch Your Free 2026 Cosmic Overview
              </Typography>
            </Box>

            <Paper sx={{ p: { xs: 1, md: 2 }, bgcolor: 'black' }}>
              <Box sx={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
                <ReactPlayer
                  url={`https://youtube.com/watch?v=${data.youtubeId}`}
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  controls={true}
                  config={{
                    youtube: {
                      playerVars: {
                        showinfo: 1,
                        modestbranding: 1,
                        rel: 0
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Paper>
        </motion.div>
      </Container>

      {/* Extended 2026 Cosmic Overview */}
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 }, px: { xs: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card sx={{ boxShadow: 4 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LockIcon sx={{ color: color, mr: 1, fontSize: 32 }} />
                  <Typography variant="h4" sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                  }}>
                    Extended 2026 Cosmic Overview
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h3" sx={{
                    color: color,
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }
                  }}>
                    $29.99
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    One-time payment
                  </Typography>
                </Box>
              </Box>

              <Typography color="text.secondary" paragraph sx={{ fontSize: { xs: '1rem', md: '1.125rem' } }}>
                Get the complete 2026 forecast for {sign?.charAt(0).toUpperCase() + sign?.slice(1)} Rising with:
              </Typography>

              <Box component="ul" sx={{ mb: 3, pl: 2, '& li': { mb: 1, fontSize: { xs: '0.95rem', md: '1.05rem' } } }}>
                <li>90+ Minute Video Deep Dive</li>
                <li>House by House Transits & Degrees impacted</li>
                <li>Month by month timing guidance for major transits</li>
                <li>Comprehensive PDF Report (request the written format FREE)</li>
                <li>Birth Chart Wheel with visual aspects</li>
                <li>Key Activation Windows</li>
                <li>House activations</li>
                <li>Lifetime access</li>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  py: { xs: 2, md: 2.5 },
                  mb: 2,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  bgcolor: color,
                  '&:hover': { bgcolor: color, opacity: 0.9 }
                }}
                onClick={() => navigate('/pricing?section=forecasts')}
              >
                View Pricing Options
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                  30-Day Money-Back Guarantee
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CosmicOverview;
